/**
 * 紹介URL(/r/<code>)の着地で保持する値の読み書き（localStorage）。Vue 非依存の純関数。
 *
 * 保持するもの:
 * - **紹介コード** … 会員登録リクエストに載せて被紹介者を紐付けるため。
 *   既存の `_YAGISH_AFFILIATE_ID`（Cookie・TTL 24h）と違い **30日** 持たせる。
 *   要件②「紹介URL経由で登録し、後日ヤギプラに登録した場合も紐付ける」を
 *   満たすには、登録が翌日以降になるケースを取りこぼせない。
 * - **visitor_key** … KPI「紹介URLからの流入 10,000UU/月」の UU を数える単位。
 *   サーバーは `referral_click_log.visitor_key` の distinct で UU を出すので、
 *   同じブラウザでは同じ値を返し続ける必要がある。
 *
 * Cookie ではなく localStorage にしているのは、読み手（会員登録の FormData 組み立て）が
 * いずれも TS モジュールで、`$cookies`（Vue.prototype 依存）を増やさずに済むため。
 * SPA と API は別オリジンだがコードは値としてリクエストに載せるので共有は不要。
 */

const REFERRAL_CODE_KEY = 'yagish_referral_code'
const REFERRAL_VISITOR_KEY = 'yagish_referral_visitor_key'

/** 紹介コードの保持期間。要件②の「後日のヤギプラ登録」を拾うための猶予 */
export const REFERRAL_CODE_TTL_MS = 30 * 24 * 60 * 60 * 1000

type StoredReferralCode = {
  code: string
  savedAt: number
}

/** Safari プライベートモード等で localStorage が投げても壊れないように包む */
function readItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

function writeItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch (e) {
    // 保存できない環境では紐付けを諦める（登録自体は妨げない）
  }
}

function removeItem(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch (e) {
    // noop
  }
}

/** サーバー発行の紹介コードとして受け付ける形。想定外の値は保存も送信もしない */
export function isValidStoredCode(code: unknown): code is string {
  return typeof code === 'string' && /^[A-Za-z0-9]{4,32}$/.test(code)
}

/** 紹介URLで着地したコードを保存する。無効な値は無視する */
export function saveReferralCode(code: string, now = Date.now()): boolean {
  if (!isValidStoredCode(code)) return false
  const payload: StoredReferralCode = { code, savedAt: now }
  writeItem(REFERRAL_CODE_KEY, JSON.stringify(payload))
  return true
}

/**
 * 保存された紹介コード。未保存・期限切れ・壊れた値はいずれも空文字。
 * 会員登録リクエストに載せる値の唯一の入口。
 */
export function loadReferralCode(now = Date.now()): string {
  const raw = readItem(REFERRAL_CODE_KEY)
  if (!raw) return ''

  let parsed: StoredReferralCode | null = null
  try {
    parsed = JSON.parse(raw) as StoredReferralCode
  } catch (e) {
    removeItem(REFERRAL_CODE_KEY)
    return ''
  }

  if (!parsed || !isValidStoredCode(parsed.code)) {
    removeItem(REFERRAL_CODE_KEY)
    return ''
  }
  if (typeof parsed.savedAt !== 'number') {
    removeItem(REFERRAL_CODE_KEY)
    return ''
  }
  if (now - parsed.savedAt > REFERRAL_CODE_TTL_MS) {
    removeItem(REFERRAL_CODE_KEY)
    return ''
  }
  return parsed.code
}

/** 紐付けが済んだあと、または期限切れで捨てるとき */
export function clearReferralCode(): void {
  removeItem(REFERRAL_CODE_KEY)
}

function randomKey(): string {
  const cryptoObj = typeof window !== 'undefined' ? window.crypto : undefined
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID()
  }
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const bytes = new Uint8Array(16)
    cryptoObj.getRandomValues(bytes)
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  }
  // crypto が無い環境向けのフォールバック。UU の粒度が多少荒くなるだけで害はない
  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 10)}`
}

/**
 * このブラウザの visitor_key。無ければ発行して保存する。
 * UU の単位なので、同じブラウザでは同じ値を返し続けることが要件。
 */
export function ensureVisitorKey(): string {
  const existing = readItem(REFERRAL_VISITOR_KEY)
  if (existing && existing.length <= 64) return existing

  const generated = randomKey().slice(0, 64)
  writeItem(REFERRAL_VISITOR_KEY, generated)
  // 保存できない環境でも、この一度のクリックは記録できるように値は返す
  return readItem(REFERRAL_VISITOR_KEY) || generated
}
